import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import { Icon } from 'components/Icon/Icon';
import { useToast } from 'components/Toast/use-toast';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INVOICE_MARK_AS_PAID, INVOICE_SEND_REMINDER } from 'services/users/mutation';

import { ConfirmInvoicePaidModal } from './ConfirmInvoicePaidModal';

type CompanyInvoicesActionsProps = {
    invoice: any;
    onUpdate: () => void;
};

export const CompanyInvoicesActions: React.FC<CompanyInvoicesActionsProps> = ({ invoice, onUpdate }) => {
    const [isActive, setIsActive] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const menu = useRef<any>(null);
    const { t } = useTranslation('documents');
    const { toast } = useToast();

    // MUTATIONS
    const [markAsPaid] = useMutation(INVOICE_MARK_AS_PAID);
    const [sendReminder] = useMutation(INVOICE_SEND_REMINDER);

    const handleClickOutside = (event: any) => {
        if (menu.current && !menu.current.contains(event.target)) {
            setIsActive(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside, true);

        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, []);

    const onDownload = () => {
        window.open(invoice?.document.url, '_blank');
    };

    const onSendReminder = async () => {
        const resp = await sendReminder({
            variables: { invoice: invoice.id },
        });

        if (resp.data?.invoiceSendReminder?.success) {
            toast({
                title: t('sendReminderSuccess'),
                variant: 'success',
            });
            onUpdate();
        } else {
            failedToast();
        }
        setIsOpen(false);
    };

    const onSetPaid = async () => {
        const resp = await markAsPaid({
            variables: { invoice: invoice.id },
        });

        if (resp.data?.invoiceMarkAsPaid?.status) {
            toast({
                title: t('markAsPaidSuccess'),
                variant: 'success',
            });
            onUpdate();
        } else {
            failedToast();
        }
        setIsOpen(false);
    };

    const failedToast = () => {
        toast({
            title: t('errorToastTitle'),
            description: t('errorToastMessage'),
            variant: 'warning',
        });
    };

    return (
        <>
            <div
                ref={menu}
                className={clsx('relative', {
                    'z-10': isActive,
                })}
            >
                <Icon
                    name="three-dots"
                    className="h-4 hover:cursor-pointer"
                    onClick={() => setIsActive(!isActive)}
                />
                {isActive && (
                    <div className="absolute right-0 top-6 bg-white border border-zinc-200 rounded-md shadow-lg py-1 min-w-[160px]">
                        <button
                            className="w-full text-left px-4 py-2 text-sm hover:bg-zinc-50 flex items-center gap-2"
                            onClick={onDownload}
                        >
                            <Icon name="download" className="h-4" />
                            {t('downloadAction')}
                        </button>
                        {invoice.status !== 'PAID' && (
                            <>
                                <button
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-zinc-50 flex items-center gap-2"
                                    onClick={onSendReminder}
                                >
                                    <Icon name="mail" className="h-4" />
                                    {t('sendReminderAction')}
                                </button>
                                <button
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-zinc-50 flex items-center gap-2"
                                    onClick={() => setIsOpen(true)}
                                >
                                    <Icon name="check" className="h-4" />
                                    {t('markAsPaidAction')}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
            <ConfirmInvoicePaidModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onConfirm={onSetPaid}
                invoice={invoice}
            />
        </>
    );
};
